import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react';
import type { PopupMenuAnchorRect, PopupMenuEntry } from '../types.ts';

const POPUP_MENU_MOBILE_MAX_WIDTH = '560px';
const POPUP_MENU_DESKTOP_POPOVER_WIDTH = '280px';

const useBeforePaintEffect: typeof useEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function usePopupMenuPositioning({
  entry,
  surfaceRef,
}: {
  entry: PopupMenuEntry;
  surfaceRef: RefObject<HTMLDivElement>;
}) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const anchorElementRef = useRef<HTMLElement | null>(null);
  const [anchorRect, setAnchorRect] = useState<PopupMenuAnchorRect | undefined>(
    undefined
  );
  const [desktopPosition, setDesktopPosition] = useState<{
    top: number;
    left: number;
    maxHeight: number;
  }>({
    top: 24,
    left: 24,
    maxHeight: 360,
  });
  const [isDesktopLargeDesktop, setIsDesktopLargeDesktop] = useState<boolean>(
    () => {
      if (typeof window === 'undefined') return false;

      return window.innerWidth >= 1024;
    }
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = (): void => {
      const nextIsDesktopLargeDesktop = window.innerWidth >= 1024;

      setIsDesktopLargeDesktop((previousIsDesktopLargeDesktop) => {
        if (!isOpen) {
          return nextIsDesktopLargeDesktop;
        }

        if (previousIsDesktopLargeDesktop !== nextIsDesktopLargeDesktop) {
          setAnchorRect(undefined);
          anchorElementRef.current = null;
          setIsOpen(false);

          return nextIsDesktopLargeDesktop;
        }

        if (nextIsDesktopLargeDesktop) {
          const anchorElement = anchorElementRef.current;

          if (anchorElement) {
            const rect = anchorElement.getBoundingClientRect();

            setAnchorRect({
              top: rect.top,
              right: rect.right,
              bottom: rect.bottom,
              left: rect.left,
              width: rect.width,
              height: rect.height,
            });
          }
        }

        return nextIsDesktopLargeDesktop;
      });
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (typeof document === 'undefined') return;

    const previousBodyOverflow = document.body.style.overflow;
    const previousBodyTouchAction = document.body.style.touchAction;
    const previousHtmlScrollbarGutter =
      document.documentElement.style.getPropertyValue('scrollbar-gutter');
    const isScrollbarTakingSpace: boolean =
      window.innerWidth > document.documentElement.clientWidth;

    if (isScrollbarTakingSpace) {
      document.documentElement.style.setProperty('scrollbar-gutter', 'stable');
    }

    document.body.style.overflow = 'hidden';
    document.body.style.touchAction = 'none';

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.body.style.touchAction = previousBodyTouchAction;
      document.documentElement.style.setProperty(
        'scrollbar-gutter',
        previousHtmlScrollbarGutter
      );
    };
  }, [isOpen]);

  useBeforePaintEffect(() => {
    if (!isOpen) return;
    if (!isDesktopLargeDesktop) return;

    const menuGap = 8;
    const edgeMargin = 12;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const surfaceHeight = surfaceRef.current?.offsetHeight || 360;
    const surfaceWidth = surfaceRef.current?.offsetWidth || 200;
    const menuWidth = Math.min(surfaceWidth, viewportWidth - edgeMargin * 2);

    const fallbackAnchor: PopupMenuAnchorRect = {
      top: viewportHeight / 2 - 16,
      right: viewportWidth / 2 + 16,
      bottom: viewportHeight / 2 + 16,
      left: viewportWidth / 2 - 16,
      width: 32,
      height: 32,
    };

    const anchor = anchorRect || fallbackAnchor;

    const spaceBelow = viewportHeight - anchor.bottom - edgeMargin;
    const spaceAbove = anchor.top - edgeMargin;
    const openBelow = spaceBelow >= surfaceHeight || spaceBelow >= spaceAbove;

    const topCandidate = openBelow
      ? anchor.bottom + menuGap
      : anchor.top - surfaceHeight - menuGap;

    const top = Math.max(
      edgeMargin,
      Math.min(topCandidate, viewportHeight - edgeMargin - 120)
    );

    const leftCandidate = anchor.right - menuWidth;
    const left = Math.max(
      edgeMargin,
      Math.min(leftCandidate, viewportWidth - menuWidth - edgeMargin)
    );

    const maxHeight = Math.max(160, viewportHeight - top - edgeMargin);

    setDesktopPosition({ top, left, maxHeight });
  }, [anchorRect, entry.title, isDesktopLargeDesktop, isOpen, surfaceRef]);

  const openFromElement = (
    element: HTMLElement,
    explicitAnchorRect?: PopupMenuAnchorRect
  ): void => {
    anchorElementRef.current = element;

    const nextAnchorRect =
      explicitAnchorRect ||
      (() => {
        const rect = element.getBoundingClientRect();

        return {
          top: rect.top,
          right: rect.right,
          bottom: rect.bottom,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        };
      })();

    setAnchorRect(nextAnchorRect);

    setIsOpen(true);
  };

  const rootStyle = useMemo(() => {
    return {
      position: 'fixed' as const,
      inset: 0,
      zIndex: 1200,
      pointerEvents: 'none' as const,
    };
  }, []);

  const surfaceStyle = useMemo(() => {
    if (!isDesktopLargeDesktop) {
      return {
        position: 'absolute' as const,
        left: '50%',
        bottom: 0,
        transform: 'translateX(-50%)',
        width: `min(calc(100% - 24px), ${POPUP_MENU_MOBILE_MAX_WIDTH})`,
        maxHeight: 'min(82vh, 680px)',
        overflowY: 'auto' as const,
        overscrollBehavior: 'contain' as const,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        pointerEvents: 'auto' as const,
      };
    }

    return {
      position: 'absolute' as const,
      top: `${desktopPosition.top}px`,
      left: `${desktopPosition.left}px`,
      width: `min(${POPUP_MENU_DESKTOP_POPOVER_WIDTH}, calc(100vw - 24px))`,
      maxHeight: `${desktopPosition.maxHeight}px`,
      overflowY: 'auto' as const,
      overscrollBehavior: 'contain' as const,
      pointerEvents: 'auto' as const,
    };
  }, [desktopPosition, isDesktopLargeDesktop]);

  return {
    isDesktopLargeDesktop,
    isOpen,
    openFromElement,
    rootStyle,
    setIsOpen,
    surfaceStyle,
  };
}
